import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { getAircraftList } from '../api';
import { useAuth } from '../auth/AuthContext';
import { useFeedback } from '../hooks/useFeedback';
import { getApiErrorMessage } from '../utils/apiError';
import { Aircraft } from '../types';
import { onJournalChanged } from '../journal/journalSync';
import {
  getCurrentAircraftId,
  onAircraftChange,
  setActiveClubId,
  setCurrentAircraftId,
  syncAircraftIdWithList,
} from './aircraftStore';

type AircraftContextValue = {
  aircraftId: number;
  aircraftList: Aircraft[];
  current: Aircraft | null;
  loading: boolean;
  /** Растёт после мутаций журнала — экраны перезагружают данные. */
  dataRevision: number;
  setAircraftId: (id: number) => void;
  refreshList: () => Promise<void>;
};

const AircraftContext = createContext<AircraftContextValue | null>(null);

const formatLabel = (item: Aircraft) => {
  const name = (item.model || '').trim();
  const reg = (item.tail_number || '').trim();
  if (name && reg) return `${name} · ${reg}`;
  return name || reg || `Борт #${item.id}`;
};

export const aircraftLabel = formatLabel;

export const AircraftProvider = ({ children }: { children: ReactNode }) => {
  const { isAuthenticated: isSignedIn, isSupport, user } = useAuth();
  // Аккаунт поддержки бортов не видит — список не запрашиваем.
  const isAuthenticated = isSignedIn && !isSupport;
  const { showError, feedbackNode } = useFeedback();
  const [aircraftId, setAircraftIdState] = useState(getCurrentAircraftId);
  const [aircraftList, setAircraftList] = useState<Aircraft[]>([]);
  // true до первой загрузки — иначе пустой список ложно блокирует навигацию
  const [loading, setLoading] = useState(true);
  const [dataRevision, setDataRevision] = useState(0);
  const listReqId = useRef(0);

  const refreshList = useCallback(async () => {
    const reqId = ++listReqId.current;
    if (!isAuthenticated) {
      setAircraftList([]);
      setAircraftIdState(0);
      setLoading(false);
      return;
    }
    try {
      const list = await getAircraftList();
      if (reqId !== listReqId.current) return;
      setAircraftList(list);
      const nextId = syncAircraftIdWithList(list.map((a) => a.id));
      setAircraftIdState(nextId);
    } catch (error) {
      if (reqId !== listReqId.current) return;
      setAircraftList([]);
      setCurrentAircraftId(0);
      setAircraftIdState(0);
      showError(getApiErrorMessage(error, 'Не удалось загрузить список бортов'));
    } finally {
      if (reqId === listReqId.current) setLoading(false);
    }
  }, [isAuthenticated, showError]);

  useEffect(() => {
    setActiveClubId(user?.club_id ?? 0);
    setAircraftIdState(getCurrentAircraftId());
    setLoading(true);
    void refreshList();
  }, [refreshList, user?.club_id]);

  useEffect(() => {
    const onUpdate = () => {
      void refreshList();
    };
    window.addEventListener('aircraft-updated', onUpdate);
    return () => window.removeEventListener('aircraft-updated', onUpdate);
  }, [refreshList]);

  useEffect(() => onAircraftChange((id) => setAircraftIdState(id)), []);

  useEffect(
    () =>
      onJournalChanged(() => {
        setDataRevision((n) => n + 1);
        // Мягко обновить hobbs в списке бортов без спиннера.
        if (!isAuthenticated) return;
        const reqId = ++listReqId.current;
        void getAircraftList()
          .then((list) => {
            if (reqId !== listReqId.current) return;
            setAircraftList(list);
            const nextId = syncAircraftIdWithList(list.map((a) => a.id));
            setAircraftIdState(nextId);
          })
          .catch(() => undefined);
      }),
    [isAuthenticated]
  );

  const setAircraftId = useCallback((id: number) => {
    setCurrentAircraftId(id);
    setAircraftIdState(id);
  }, []);

  const current = useMemo(
    () => aircraftList.find((a) => a.id === aircraftId) || null,
    [aircraftList, aircraftId]
  );

  const value = useMemo(
    () => ({
      aircraftId,
      aircraftList,
      current,
      loading,
      dataRevision,
      setAircraftId,
      refreshList,
    }),
    [
      aircraftId,
      aircraftList,
      current,
      loading,
      dataRevision,
      setAircraftId,
      refreshList,
    ]
  );

  return (
    <AircraftContext.Provider value={value}>
      {feedbackNode}
      {children}
    </AircraftContext.Provider>
  );
};

export const useAircraft = (): AircraftContextValue => {
  const ctx = useContext(AircraftContext);
  if (!ctx) {
    throw new Error('useAircraft must be used within AircraftProvider');
  }
  return ctx;
};
